import { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import Layout from '../components/Layout'
import { useWallet } from '../contexts/WalletContext'
import { useToast } from '../contexts/ToastContext'
import CreateDuelModal from '../components/CreateDuelModal'
import MatchDuelModal from '../components/MatchDuelModal'
import { apiFetch } from '../config/api'
import { transferViaAndroid, detectPlatform, ESCROW_ADDRESS } from '../utils/transfer'
import { formatCHNX } from '../utils/formatCHNX'

interface Match {
  matchId: string
  title: string
  sideA: string
  sideB: string
  matchTime: string
  venue: string
  sportType: string
  status: string
  totalPool: string
  activeDuels: number
  sideAPool: string
  sideBPool: string
  judgeAddress: string
}

interface Duel {
  duelId: string
  matchId: string
  matchTitle?: string
  sideA?: string
  sideB?: string
  matchTime?: string
  creatorAddress: string
  creatorSide: 'sideA' | 'sideB'
  amount: string
  status: string
  createdAt: string
}

export default function MatchDetail() {
  const { matchId } = useParams()
  const navigate = useNavigate()
  const { address: walletAddress, refreshBalance } = useWallet()
  const toast = useToast()
  const [match, setMatch] = useState<Match | null>(null)
  const [openDuels, setOpenDuels] = useState<Duel[]>([])
  const [loading, setLoading] = useState(true)
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [selectedDuel, setSelectedDuel] = useState<Duel | null>(null)

  useEffect(() => {
    if (matchId) {
      fetchMatchDetail()
      fetchOpenDuels()
    }
  }, [matchId])

  const fetchMatchDetail = async () => {
    try {
      const response = await fetch(`/api/duel/matches/${matchId}`)
      const data = await response.json()
      if (data.success) {
        setMatch(data.data)
      }
    } catch (error) {
      console.error('Failed to fetch match detail:', error)
    } finally {
      setLoading(false)
    }
  }

  const fetchOpenDuels = async () => {
    try {
      const response = await fetch(`/api/duel/open?matchId=${matchId}`)
      const data = await response.json()
      if (data.success) {
        setOpenDuels(data.data.duels || [])
      }
    } catch (error) {
      console.error('Failed to fetch open duels:', error)
    }
  }

  const handleCreateDuel = async (side: 'sideA' | 'sideB', amount: string) => {
    try {
      const pageVersion = '2024-12-18 22:45:00';
      console.log('==============================================');
      console.log('[CreateDuel] VERSION:', pageVersion);
      console.log('[CreateDuel] Start -', { matchId, side, amount, walletAddress });
      
      // Step 1: Check platform
      const platform = detectPlatform();
      if (!platform.isSupported) {
        toast.error('Platform not supported. Please use the mobile app.');
        return;
      }
      
      // Step 2: Transfer tokens to escrow
      toast.info('Step 1/2: Transferring CHNX to Escrow...');
      console.log('[CreateDuel] Transferring', amount, 'CHNX to', ESCROW_ADDRESS);
      
      let lockTxHash: string;
      
      try {
        if (platform.isAndroid) {
          lockTxHash = await transferViaAndroid(ESCROW_ADDRESS, amount);
          console.log('[CreateDuel] Transfer complete, lockTxHash:', lockTxHash);
          
          if (!lockTxHash || lockTxHash.trim() === '') {
            throw new Error('Transfer did not return a valid transaction hash');
          }
        } else {
          throw new Error('MetaMask transfer not yet implemented');
        }
      } catch (transferError: any) {
        console.error('[CreateDuel] Transfer failed:', transferError.message);
        toast.error(`Transfer failed: ${transferError.message}`);
        return;
      }
      
      // Step 3: Call API to create duel record
      toast.info('Step 2/2: Creating prediction record...');
      console.log('[CreateDuel] Creating duel with lockTxHash:', lockTxHash);
      console.log('[CreateDuel] 🔥🔥🔥 VERSION: 2024-12-18 23:50:00 🔥🔥🔥');
      
      const reqHeaders = {
        'Content-Type': 'application/json',
        'X-Lock-TxHash': lockTxHash
      };
      console.log('[CreateDuel] reqHeaders created:', JSON.stringify(reqHeaders));
      console.log('[CreateDuel] reqHeaders["X-Lock-TxHash"]:', reqHeaders['X-Lock-TxHash']);
      
      const response = await apiFetch('/duel/create', {
        method: 'POST',
        headers: reqHeaders,
        body: JSON.stringify({
          matchId,
          side,
          amount,
          walletAddress,
          lockTxHash
        }),
      });
      
      const data = await response.json();
      
      if (data.success) {
        console.log('[CreateDuel] Success, duelId:', data.data.duelId);
        toast.success(`Prediction created! TX: ${lockTxHash.substring(0, 10)}...`);
        setShowCreateModal(false);
        fetchMatchDetail();
        fetchOpenDuels();
        refreshBalance();
      } else {
        console.error('[CreateDuel] API error:', data.error);
        toast.error(`Failed to create prediction: ${data.error}`);
      }
    } catch (error: any) {
      console.error('[CreateDuel] Error:', error.message);
      toast.error(error.message || 'Network error, please try again');
    }
  }

  const handleMatchDuel = async (duelId: string, amount: string) => {
    try {
      console.log('🎯 [MatchDuel] Starting duel match', { 
        duelId, 
        amount, 
        walletAddress,
        escrowAddress: ESCROW_ADDRESS
      });
      
      // Step 1: Check platform
      const platform = detectPlatform();
      console.log('🔍 [MatchDuel] Platform detected:', platform);
      
      if (!platform.isSupported) {
        toast.error('Platform not supported. Please use the mobile app.');
        return;
      }
      
      // Step 2: Transfer tokens to escrow
      toast.info('Step 1/2: Transferring CHNX to Escrow...');
      console.log('💸 [MatchDuel] Starting transfer to escrow', {
        from: walletAddress,
        to: ESCROW_ADDRESS,
        amount
      });
      
      let lockTxHash: string;
      
      try {
        if (platform.isAndroid) {
          lockTxHash = await transferViaAndroid(ESCROW_ADDRESS, amount);
          console.log('✅ [MatchDuel] Android transfer successful:', lockTxHash);
          
          // Validate lockTxHash
          if (!lockTxHash || lockTxHash === 'undefined' || lockTxHash.trim() === '') {
            throw new Error('Transfer returned invalid transaction hash');
          }
        } else {
          throw new Error('MetaMask transfer not yet implemented');
        }
      } catch (transferError: any) {
        console.error('❌ [MatchDuel] Transfer failed:', transferError);
        toast.error(`Transfer failed: ${transferError.message}`);
        return;
      }
      
      // Verify lockTxHash before proceeding
      if (!lockTxHash) {
        console.error('❌ [MatchDuel] lockTxHash is missing after transfer');
        toast.error('Transaction hash not received. Please try again.');
        return;
      }
      
      // Step 3: Call API to match duel
      toast.info('Step 2/2: Matching prediction...');
      
      console.log('📤 [MatchDuel] Sending API request with lockTxHash:', lockTxHash);
      const response = await apiFetch(`/duel/match/${duelId}`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'X-Lock-TxHash': lockTxHash  // Bypass JWT Interceptor body parsing issue
        },
        body: JSON.stringify({
          walletAddress,
          amount,
          lockTxHash
        }),
      })
      
      const data = await response.json()
      
      if (data.success) {
        console.log('✅ [MatchDuel] Duel matched successfully');
        toast.success(`Prediction matched! TX: ${lockTxHash.substring(0, 10)}...`);
        setSelectedDuel(null);
        fetchMatchDetail();
        fetchOpenDuels();
        // Refresh balance
        refreshBalance();
      } else {
        console.error('❌ Match duel failed:', data.error);
        toast.error(`Failed to match prediction: ${data.error}`);
      }
    } catch (error: any) {
      console.error('❌ Match duel error:', error);
      toast.error(error.message || 'Network error, please try again');
    }
  }

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-lg text-gray-600">Loading match details...</div>
      </div>
    )
  }

  if (!match) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500 text-lg">Match not found</p>
        <button
          onClick={() => navigate('/')}
          className="mt-4 text-indigo-600 hover:text-indigo-700"
        >
          ← Back to matches
        </button>
      </div>
    )
  }

  return (
    <Layout>
      <div>
        {/* Version Info */}
        <div className="mb-2 text-xs text-gray-400 text-right">
          Code Version: 2024-12-18 22:45:00
        </div>
        
        {/* Back Button */}
        <button
        onClick={() => navigate('/')}
        className="mb-4 text-indigo-600 hover:text-indigo-700 flex items-center gap-2"
      >
        ← Back to matches
      </button>

      {/* Match Header */}
      <div className="bg-white rounded-lg shadow-md p-6 mb-6">
        <div className="flex justify-between items-start mb-4">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">
              {match.sideA} vs {match.sideB}
            </h1>
            <div className="flex gap-4 text-sm text-gray-600">
              <span>🕐 {new Date(match.matchTime).toLocaleString()}</span>
              <span>🏟️ {match.venue}</span>
              <span>🏀 {match.sportType}</span>
            </div>
          </div>
          <span className={`px-3 py-1 rounded-full text-sm font-medium ${
            match.status === 'upcoming' ? 'bg-green-100 text-green-800' :
            match.status === 'live' ? 'bg-blue-100 text-blue-800' :
            'bg-gray-100 text-gray-800'
          }`}>
            {match.status}
          </span>
        </div>

        {/* Pool Stats */}
        <div className="grid grid-cols-3 gap-4 mt-6 pt-6 border-t">
          <div className="text-center">
            <p className="text-sm text-gray-600 mb-1">Total Pool</p>
            <p className="text-2xl font-bold text-gray-900">{formatCHNX(match.totalPool)} CHNX</p>
          </div>
          <div className="text-center">
            <p className="text-sm text-gray-600 mb-1">{match.sideA} Pool</p>
            <p className="text-2xl font-bold text-blue-600">{formatCHNX(match.sideAPool)} CHNX</p>
          </div>
          <div className="text-center">
            <p className="text-sm text-gray-600 mb-1">{match.sideB} Pool</p>
            <p className="text-2xl font-bold text-red-600">{formatCHNX(match.sideBPool)} CHNX</p>
          </div>
        </div>

        {/* Create Duel Button */}
        {match.status === 'completed' || match.status === 'cancelled' ? (
          <div className="w-full mt-6 bg-gray-300 text-gray-500 px-6 py-3 rounded-lg text-center font-medium cursor-not-allowed">
            ⛔ Match {match.status === 'completed' ? 'Completed' : 'Cancelled'} - Cannot Create Duel
          </div>
        ) : (
          <button
            onClick={() => setShowCreateModal(true)}
            className="w-full mt-6 bg-indigo-600 text-white px-6 py-3 rounded-lg hover:bg-indigo-700 transition-colors font-medium"
          >
            🎯 Create New Duel
          </button>
        )}
      </div>

      {/* Open Duels */}
      <div className="bg-white rounded-lg shadow-md p-6">
        <h2 className="text-2xl font-bold text-gray-900 mb-4">
          Open Duels ({openDuels.length})
        </h2>

        {openDuels.length === 0 ? (
          <div className="text-center py-8">
            <p className="text-gray-500">No open duels available</p>
            <p className="text-gray-400 text-sm mt-2">Be the first to create one!</p>
          </div>
        ) : (
          <div className="space-y-4">
            {openDuels.map((duel) => (
              <div
                key={duel.duelId}
                className="border border-gray-200 rounded-lg p-4 hover:border-indigo-300 transition-colors"
              >
                <div className="flex justify-between items-center">
                  <div>
                    <p className="font-medium text-gray-900">
                      Predicting: <span className={duel.creatorSide === 'sideA' ? 'text-blue-600' : 'text-red-600'}>
                        {duel.creatorSide === 'sideA' ? match.sideA : match.sideB}
                      </span>
                    </p>
                    <p className="text-sm text-gray-600 mt-1">
                      Amount: <span className="font-semibold">{formatCHNX(duel.amount)} CHNX</span>
                    </p>
                    <p className="text-xs text-gray-500 mt-1">
                      Creator: {duel.creatorAddress.slice(0, 6)}...{duel.creatorAddress.slice(-4)}
                    </p>
                    <p className="text-xs text-gray-500">
                      Created: {new Date(duel.createdAt).toLocaleString()}
                    </p>
                  </div>
                  
                  {duel.creatorAddress.toLowerCase() !== (walletAddress || '').toLowerCase() ? (
                    <button
                      onClick={() => setSelectedDuel(duel)}
                      className="bg-green-600 text-white px-6 py-2 rounded-md hover:bg-green-700 transition-colors font-medium"
                    >
                      Match Duel
                    </button>
                  ) : (
                    <span className="text-sm text-gray-500 italic">Your duel</span>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Modals */}
      {showCreateModal && (
        <CreateDuelModal
          match={match}
          onClose={() => setShowCreateModal(false)}
          onSubmit={handleCreateDuel}
        />
      )}

      {selectedDuel && (
        <MatchDuelModal
          duel={selectedDuel}
          match={match}
          onClose={() => setSelectedDuel(null)}
          onSubmit={(amount) => handleMatchDuel(selectedDuel.duelId, amount)}
        />
      )}
      </div>
    </Layout>
  )
}
